import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import CoverImage from "@/components/marketing/CoverImage";

function renderCard({ project, className = "", aspect = "aspect-[4/5]" }) {
  return (
    <Link
      to={`/portfolio/${project.slug}`}
      className={`group block ${className}`}
      data-testid={`selected-work-card-${project.slug}`}
    >
      <div className={`photo-frame ${aspect} bg-neutral-900`}>
        <CoverImage
          src={project.cover_image}
          fallbackSrc={(project.gallery || [])[0]}
          alt={project.alt_text || project.title}
          title={project.title}
          testId={`selected-work-cover-${project.slug}`}
        />
      </div>
      <div className="mt-4 flex items-baseline justify-between gap-4">
        <div>
          <h3 className="font-editorial italic text-2xl md:text-3xl">{project.title}</h3>
          <p className="label-mono opacity-60 mt-1">
            {project.category_slug?.replace("-", " ")} · {project.location}
          </p>
        </div>
        <p className="label-mono opacity-60">{project.date_or_year}</p>
      </div>
    </Link>
  );
}

/**
 * Selected work - editorial masonry layout. Mixes portrait, landscape and paired images.
 * Loads featured projects from API.
 */
export default function SelectedWork() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api
      .get("/projects", { params: { featured: true } })
      .then(({ data }) => setProjects(data || []))
      .catch(() => {});
  }, []);

  if (!projects.length) return null;
  const [a, b, c, ...rest] = projects;
  const Card = renderCard;

  return (
    <section
      data-testid="selected-work"
      className="bg-ink text-ivory py-24 md:py-40"
    >
      <div className="mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-end justify-between mb-16 md:mb-24">
          <div>
            <p className="label-mono opacity-60 line-badge">01 — Selected Work</p>
            <h2 className="mt-6 font-editorial text-5xl md:text-7xl lg:text-8xl leading-[0.95]">
              Recent <em className="not-italic md:italic">stories</em>.
            </h2>
          </div>
          <Link
            to="/portfolio"
            className="hidden md:inline-flex hover-underline label-mono"
            data-testid="selected-work-view-all"
          >
            View full portfolio →
          </Link>
        </div>

        <motion.div
          className="grid grid-cols-12 gap-4 md:gap-8"
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.1 }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.12 } },
          }}
        >
          {a && (
            <motion.div
              className="col-span-12 md:col-span-7"
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.19, 1, 0.22, 1] } },
              }}
            >
              <Card project={a} aspect="aspect-[16/11]" />
            </motion.div>
          )}
          {b && (
            <motion.div
              className="col-span-12 md:col-span-5 md:mt-24"
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.19, 1, 0.22, 1] } },
              }}
            >
              <Card project={b} aspect="aspect-[3/4]" />
            </motion.div>
          )}
          {c && (
            <motion.div
              className="col-span-12 md:col-span-8 md:col-start-3"
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.19, 1, 0.22, 1] } },
              }}
            >
              <Card project={c} aspect="aspect-[16/9]" />
            </motion.div>
          )}
          {rest.map((p, i) => (
            <motion.div
              key={p.id || p.slug}
              className={i % 2 === 0 ? "col-span-12 md:col-span-6" : "col-span-12 md:col-span-6 md:mt-16"}
              variants={{
                hidden: { opacity: 0, y: 60 },
                show: { opacity: 1, y: 0, transition: { duration: 1, ease: [0.19, 1, 0.22, 1] } },
              }}
            >
              <Card project={p} aspect={i % 2 === 0 ? "aspect-[4/5]" : "aspect-[16/11]"} />
            </motion.div>
          ))}
        </motion.div>

        <div className="mt-14 md:hidden">
          <Link to="/portfolio" className="btn-outline-ivory" data-testid="selected-work-view-all-mobile">
            View full portfolio
          </Link>
        </div>
      </div>
    </section>
  );
}
